/**
 * The Certification & Compliance page, `/why-ryerson/ryerson-commitment/certification-compliance/`
 * (Ryerson build only): a header band, then three `AllSections` sections: the standardized
 * procedure (ISO 9001 and ASME) certificates, the compliance certifications, and the
 * environmental health certifications. The documents it offers are PDFs served from
 * `/static-assets/documents/pdfs/<language>/…`, which the web-static-assets repository owns.
 *
 * In this repository it is linked from the landing-page teaser
 * (`components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/Content.tsx`), the Why
 * Ryerson commitment cards (`components/Shared/RyersonCommitment/Content.tsx`) and the "Metal
 * Certifications" cards in `content/ryerson/metal-resources/tools-calculators/index.ts` and
 * `pages/metal-resources/references/index.tsx`. The SEO
 * fields come from `content/ryerson/why-ryerson/ryerson-commitment/certification-compliance.ts`,
 * whose `PageContent` `scripts/staticSearch.ts` lists in the site-search index.
 *
 * Each section title is read from that section's own copy module, so the section anchors (the
 * slug rule in `components/Shared/AllSections/AllSections.tsx`) differ by language. The compliance
 * section also carries the fixed `#ryerson-canada-labor-report` anchor
 * (`ComplianceCertifications/ComplianceCertificationsDesktop.tsx`).
 */
import React from "react";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import CertificationAndComplianceHeader from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/CertificationAndComplianceHeader/CertificationAndComplianceHeader";
import StandardizedProcedureCertifications from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/StandardizedProcedureCertifications/StandardizedProcedureCertifications";
import ComplianceCertifications from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/ComplianceCertifications/ComplianceCertifications";
import EnvironmentalHealthCerts from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/EnvironmentalHealthCerts/EnvironmentalHealthCerts";
import { StandardizedProcedureCertificationsContent } from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/StandardizedProcedureCertifications/Content";
import { ComplianceCertificationsContent } from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/ComplianceCertifications/ComplianceCertificationsContent";
import { EnvironmentalHealthCertsContent } from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/EnvironmentalHealthCerts/EnvironmentalHealthCertsContent";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/why-ryerson/ryerson-commitment/certification-compliance";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/** Builds the page for the Ryerson brand only, with the Contentful header and footer data. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** Renders the page in the visitor's language; see the file header. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const sections: Sections[] = [
		{
			title: StandardizedProcedureCertificationsContent[language].title,
			cmp: <StandardizedProcedureCertifications />,
		},
		{
			title: ComplianceCertificationsContent[language].main.title,
			cmp: <ComplianceCertifications />,
			// Mobile only: raises the open accordion tab's clip height from `AccordionTab`'s
			// 2000px default (`overflow: hidden`), so the six-item list is not cut off.
			maxHeight: "15000px",
		},
		{
			title: EnvironmentalHealthCertsContent[language].main.title,
			cmp: <EnvironmentalHealthCerts />,
		},
	];

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<CertificationAndComplianceHeader />
			<AllSections sections={sections} />
		</HeaderLayout>
	);
};

export default Page;
